// =====================================================================================
// INVRT Radar — web console — first-membership claim (W6-03-B)
//
// The page a signed-in user with NO memberships lands on (routed from App.tsx) instead of
// the shell's dead end. It lists the organisations whose APPROVED signup matches this
// session's own verified address — the server does the matching and re-verifies every
// rule on the claim itself; this page asserts nothing.
//
// Minimal by owner ruling (14 Aug 2026): a clean card per organisation with one confirm
// button, an honest empty state, and nothing else — no new navigation, no extra chrome.
// On success the memberships are refetched and the claimed organisation selected, so the
// OrgSwitcher populates and every surface (the Credits tab included) resolves.
// =====================================================================================

import { useState } from "react";
import { LogOut } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { useClaimableOrgs, useClaimOrg } from "@/hooks/useClaims";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { isApiError } from "@/lib/api";
import { copyForToken } from "@/lib/errorTokens";
import { formatDate } from "@/lib/format";
import type { ClaimableOrg } from "@/hooks/useClaims";

/** The three claim refusals, in words a person can act on. Anything else falls back to the house token map. */
const CLAIM_FAILURE_COPY: Record<string, string> = {
  claim_not_matched:
    "This organisation cannot be activated from the address you signed in with. Sign in with the address that approved it, or ask your organisation's admin to add you.",
  claim_org_staffed:
    "This organisation already has members, so it cannot be claimed. Ask one of its administrators to add you.",
  claim_org_closed: "This organisation has been closed, so there is nothing to activate.",
};

function failureCopyFor(error: unknown): string {
  if (isApiError(error)) {
    return CLAIM_FAILURE_COPY[error.token] ?? copyForToken(error.token).body;
  }
  return copyForToken(undefined).body;
}

interface ClaimFailure {
  orgId: string;
  message: string;
}

export function ClaimOrgsPage() {
  const { session, refreshMemberships, signOut } = useSession();
  const claimable = useClaimableOrgs();
  const claim = useClaimOrg();
  const [claimingOrgId, setClaimingOrgId] = useState<string | null>(null);
  const [failure, setFailure] = useState<ClaimFailure | null>(null);

  async function activate(org: ClaimableOrg): Promise<void> {
    setFailure(null);
    setClaimingOrgId(org.org_id);
    try {
      await claim.mutateAsync({ org_id: org.org_id });
      // The memberships refetch decides the org scope, not this page: under the R7
      // tenant scope guard the SessionProvider pins a non-super-admin to their first
      // membership and discards any remembered choice (only a super-admin's is
      // honoured), so a pre-selection here would be a dead write. A claimant arrives
      // with zero memberships, so that pin lands on the organisation just claimed.
      await refreshMemberships();
      // Once memberships hold a row, App.tsx routes away from this page by itself.
    } catch (error) {
      setFailure({ orgId: org.org_id, message: failureCopyFor(error) });
      // A refusal can mean the list has moved on (an org staffed since it was listed) —
      // re-ask the server rather than keep offering a claim it would refuse again.
      await claimable.refetch();
    } finally {
      setClaimingOrgId(null);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <p className="text-2xl font-semibold tracking-tight text-white">
            INVRT <span className="text-accent">Radar</span>
          </p>
          <p className="mt-2 text-sm text-ink-faint">
            Signed in as <span className="font-medium">{session?.user.email}</span>
          </p>
        </div>

        {/* isPending, not isLoading: before the session resolves the query is DISABLED —
            isLoading is false then, and the empty state must not flash before the server
            has actually been asked. isPending stays true until data first lands. */}
        {claimable.isPending ? (
          <Card>
            <CardContent className="pt-5">
              <LoadingState label="Checking which organisations you can activate…" />
            </CardContent>
          </Card>
        ) : claimable.isError ? (
          <ErrorState error={claimable.error} onRetry={() => void claimable.refetch()} />
        ) : (claimable.data?.claimable.length ?? 0) === 0 ? (
          <EmptyState
            title="Your account is not a member of any organisation yet."
            body="If your organisation already uses Radar, ask your organisation's admin to add you. If you approved a signup with a different email address, sign in with that address instead."
            className="border-white/20 bg-surface"
          />
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-center text-sm text-ink-faint">
              You can activate the organisations below — each was approved from the address
              you signed in with.
            </p>
            {claimable.data?.claimable.map((org) => (
              <Card key={org.org_id}>
                <CardHeader>
                  <CardTitle>{org.org_name}</CardTitle>
                  <CardDescription>Approved {formatDate(org.approved_at)}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  {failure?.orgId === org.org_id ? (
                    <p role="alert" className="text-sm text-danger">
                      {failure.message}
                    </p>
                  ) : null}
                  <Button
                    className="w-full"
                    loading={claimingOrgId === org.org_id}
                    disabled={claimingOrgId !== null && claimingOrgId !== org.org_id}
                    onClick={() => void activate(org)}
                  >
                    Activate {org.org_name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex items-center gap-2 text-xs text-ink-faint hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
